export interface Bonus {
  number: number
  title: string
  shortTitle: string
  pages: number
  subtitle: string
  icon: 'builder' | 'playbook' | 'meet' | 'tracker' | 'scripts'
  /** Front cover of the kit (matches the product mockup). */
  cover: string
  value: string
  items: string[]
  why?: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'The 10-Minute Practice Builder',
    shortTitle: 'The 10-Minute Practice Builder',
    pages: 24,
    subtitle: 'Build a clean, organized practice in minutes — even with mixed groups',
    icon: 'builder',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/17e649d3-405f-4193-8ff5-dde5cf96243f.jpg',
    value: '$47',
    items: [
      'The 5-part practice formula (every effective session follows this)',
      'Templates for 30, 45, 60 and 90-minute practices',
      'Mixed-group station rotation system',
      'The one-focus rule (simplify coaching for better results)',
      'Practice priority matrix (adjust based on athlete condition)',
      'Practice examples across all events (sprints, jumps, throws, distance, hurdles)',
      'Emergency practice plans (weather changes, time constraints)',
      'Printable daily practice planner',
      'Quick reference sheet',
    ],
  },
  {
    number: 2,
    title: 'The Low-Resource Track Coach Playbook',
    shortTitle: 'The Low-Resource Coach Playbook',
    pages: 24,
    subtitle: 'Train athletes effectively with limited space, equipment and staff',
    icon: 'playbook',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/ade83dd2-367d-4885-99d1-a991c89a37ba.jpg',
    value: '$47',
    items: [
      'Resource audit (identify what you actually have)',
      'Minimal equipment kit ($50-100 to get started)',
      'Sprint adaptations (no blocks, no track, no timing gates)',
      'Hurdle adaptations (use cones instead)',
      'Jump adaptations (no sand pit? Here’s what to do)',
      'Throw adaptations (safe alternatives to full implements)',
      'Distance adaptations (time-based instead of measured)',
      'One-coach team management strategies',
      'Large group adaptations',
      'Rainy day practice plans',
      'Session adaptation worksheet',
      'Low-resource weekly plan example',
    ],
    why: 'Not every coach has a professional track. Many coaches work alone. Many have shared facilities. This bonus makes your system usable in real-world conditions.',
  },
  {
    number: 3,
    title: 'The Meet Day Command Kit',
    shortTitle: 'The Meet Day Command Kit',
    pages: 26,
    subtitle: 'Complete system for organized, stress-free competition days',
    icon: 'meet',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/5c10b91e-9586-4479-8c90-170b76d815a0.jpg',
    value: '$47',
    items: [
      '48-hour pre-meet checklist',
      '24-hour pre-meet checklist',
      'Coach competition bag checklist',
      'Athlete meet bag checklist',
      'Arrival protocol and team area setup',
      'Universal warm-up protocol (20-30 minutes)',
      'Event-specific warm-ups (sprints, hurdles, jumps, throws, distance)',
      'Event timing planner (when to warm up for every athlete)',
      'Managing athletes with multiple events',
      'Competition cue cards (one cue per event)',
      'Coach behavior guidelines (do’s and don’ts)',
      'Emergency decision tree',
      'Between-event recovery plan',
      'Post-event review structure',
      'Post-meet team review script',
      'Performance tracking sheet',
      'Parent communication templates',
      'Printable meet day dashboard',
    ],
  },
  {
    number: 4,
    title: 'The Athlete Readiness & Progress Tracker',
    shortTitle: 'The Athlete Readiness & Progress Tracker',
    pages: 24,
    subtitle:
      'Monitor readiness, fatigue, progress and performance — printable workbook',
    icon: 'tracker',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/8792e601-e468-4e85-90ea-a6897dabf787.jpg',
    value: '$37',
    items: [
      'The 3-score readiness system (sleep, soreness, motivation)',
      'Readiness zones (green/yellow/red and what to do)',
      'Daily readiness check sheet',
      'Weekly athlete check-in form',
      'Weekly team readiness dashboard',
      'Athlete profile sheet',
      'Event-specific performance logs (sprints, hurdles, jumps, throws, distance)',
      'Monthly testing tracker',
      'Personal best tracker',
      'Competition result log',
      'Injury & soreness flag sheet',
      'Training adjustment guide',
      '4-week athlete progress review',
      'Monthly team review template',
      'Coach observation notes',
      'Printable quick dashboard',
    ],
    why: 'Great coaches don’t guess—they track. This workbook gives you a simple system to monitor readiness, identify fatigue before it becomes a problem, and make smarter training decisions.',
  },
  {
    number: 5,
    title: 'The Coach Communication Script Pack',
    shortTitle: 'The Coach Communication Script Pack',
    pages: 24,
    subtitle:
      'Exactly what to say to athletes, parents and your team in every situation',
    icon: 'scripts',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/a0f7633b-cdff-4c9a-afff-3a1728d5a134.jpg',
    value: '$37',
    items: [
      'The 4-part communication formula',
      'Preseason team message script',
      'First practice welcome script',
      'Athlete goal-setting script',
      'Before a hard practice script',
      'Before an easy practice script',
      'Pre-meet team script',
      'Individual pre-competition script',
      'After great performance script',
      'After poor performance script',
      'Athlete lacking confidence script',
      'Athlete overtraining warning script',
      'Injury conversation script',
      'Parent expectation message',
      'Parent concern response script',
      'Team accountability script',
      'Team motivation script',
      'Relay team communication script',
      'End-of-season message script',
      '20+ quick script cards (one-liners)',
      'Blank script builder (create your own)',
    ],
    why: 'Communication shapes how athletes understand training, pressure, failure and themselves. These scripts eliminate confusion and build confidence.',
  },
]
